import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import {
  IOsiAccrualsByAbonentViewModel,
  IOsiAccrualsByAbonentViewModelToken
} from '@shared/types/mobx/widgets/OsiAccrualsByAbonentWidget';
import { useMemo } from 'react';
import { tokens, useTranslation } from '@shared/utils/i18n';
// Stack добавлен в импорт для размещения DatePicker и чекбокса в одном ряду с фиксированным зазором
import { Autocomplete, Box, Checkbox, FormControlLabel, Grid, Stack, TextField } from '@mui/material';
import { flatComparator } from '@shared/utils/helpers/flatComparator';
import { DatePicker } from '@mui/lab';
import { AccrualsTable } from '@shared/common/AccrualsTable';
import { darken, lighten } from '@mui/material/styles';

const getBackgroundColor = (color: any, mode: any) => (mode === 'dark' ? darken(color, 0.6) : lighten(color, 0.6));

const getHoverBackgroundColor = (color: any, mode: any) =>
  `${mode === 'dark' ? darken(color, 0.5) : lighten(color, 0.5)} !important`;

export const OsiAccrualsByAbonentWidget: React.FC = observer(() => {
  const vm = useInjection<IOsiAccrualsByAbonentViewModel>(IOsiAccrualsByAbonentViewModelToken);

  const { translateToken: tt, t } = useTranslation();

  const columns = useMemo(
    () => [
      { field: 'id', hide: true },
      { field: 'level', hide: true },
      {
        field: 'service',
        headerName: t('osv:tableByAbonent.service'),
        minWidth: 160,
        valueGetter: ({ row }: { row: any }) => {
          const { groupName } = row;
          return groupName[groupName.length - 1];
        },
        flex: 0.3
      },
      {
        field: 'totalAccrual',
        headerName: t('osv:tableByAbonent.totalDebt'),
        headerAlign: 'left',
        type: 'number',
        align: 'left',
        minWidth: 50,
        valueGetter: (params: any) => (typeof params.value === 'number' ? params.value.toFixed(2) : params.value),
        flex: 0.2
      },
      {
        field: 'totalFix',
        headerName: t('osv:tableByAbonent.totalFixes'),
        headerAlign: 'left',
        type: 'number',
        align: 'left',
        minWidth: 50,
        valueGetter: (params: any) => (typeof params.value === 'number' ? params.value.toFixed(2) : params.value),
        flex: 0.2
      },
      {
        field: 'totalTotal',
        headerName: t('osv:tableByAbonent.total'),
        headerAlign: 'left',
        type: 'number',
        align: 'left',
        minWidth: 50,
        valueGetter: (params: any) => (typeof params.value === 'number' ? params.value.toFixed(2) : params.value),
        flex: 0.2
      }
    ],
    [tt]
  );

  return (
    <>
      <Box sx={{ mt: 2 }} display="flex" alignItems="center">
        <Grid container spacing={1}>
          <Grid item xs={12} sm={3}>
            <Autocomplete
              disabled={vm.isLoading}
              fullWidth
              disableClearable
              getOptionLabel={(option) => option.flat}
              renderOption={(props, option) => <li {...props}>{option.flat}</li>}
              options={vm.visibleAbonents?.slice()?.sort((a, b) => flatComparator(a.flat, b.flat))}
              onChange={async (event, value) => {
                vm.selectedAbonent = value;
              }}
              // @ts-expect-error autocomplete work with null
              value={vm.selectedAbonent}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={tt(tokens.common.formFields.selectFlat.label)}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    autoComplete: 'new-password'
                  }}
                  style={{ margin: 0 }}
                />
              )}
            />
          </Grid>
          {/* Auto-ширина Grid item (без адаптивных sm), чтобы чекбокс не растягивался по колонке
              и держался рядом с DatePicker с фиксированным зазором независимо от ширины окна */}
          <Grid item xs={12} sm="auto">
            {/* DatePicker и чекбокс в одном ряду с ФИКСИРОВАННЫМ зазором spacing={2} (16px),
                выравнивание по левому краю (justifyContent не задаётся → flex-start) */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ height: '100%' }}>
              <DatePicker
                disabled={vm.isLoading}
                views={['year', 'month']}
                openTo={'month'}
                label={tt(tokens.common.formFields.month.label)}
                minDate={new Date('2021-03-01')}
                maxDate={new Date('2031-03-01')}
                value={vm.dateValue}
                onAccept={async (date: Date | null) => {
                  vm.dateValue = date;
                }}
                onChange={(date: Date | null) => null}
                renderInput={(params) => <TextField size="small" {...params} helperText={null} />}
              />
              {/* Чекбокс «Свернуть дочерние помещения» — скрывает паркинги/кладовки из списка
                  и вливает их начисления в начисления родителя; m:0 убирает дефолтный отрицательный
                  margin FormControlLabel, чтобы зазор задавался только Stack spacing.
                  OSI-806: показываем ТОЛЬКО если у ОСИ реально есть дочерние помещения (vm.hasChildAbonents,
                  флаг приходит с бэка в osiInfo). В домах без паркингов/кладовок чекбокс бессмыслен.
                  Значение vm.isCollapseChildren при этом НЕ сбрасываем — сворачивать всё равно нечего. */}
              {vm.hasChildAbonents && (
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      checked={vm.isCollapseChildren}
                      onChange={(e) => vm.setCollapseChildren(e.target.checked)}
                      disabled={vm.isLoading}
                    />
                  }
                  label={tt(tokens.osiOSV.collapseChildren)}
                />
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>
      <Box
        sx={{
          height: '60vh',
          '& .row-child--level-1': {
            bgcolor: (theme) => getBackgroundColor(theme.palette.info.main, theme.palette.mode),
            '&:hover': {
              bgcolor: (theme) => getHoverBackgroundColor(theme.palette.info.main, theme.palette.mode)
            }
          }
        }}
      >
        <AccrualsTable rows={vm.rows} columns={columns} isLoading={vm.isLoading} />
      </Box>
    </>
  );
});
